import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./EducationLoan.css";
import "animate.css/animate.min.css";
import Seo from "../components/Seo";

const educationLoanUses = [
  {
    icon: "bi bi-mortarboard",
    title: "Higher Education in India",
    text:
      "Finance eligible undergraduate, postgraduate, professional and specialised courses at recognised institutions.",
  },
  {
    icon: "bi bi-globe2",
    title: "Studies Abroad",
    text:
      "Explore funding for eligible overseas education, including tuition, travel and approved living expenses.",
  },
  {
    icon: "bi bi-building",
    title: "Tuition and Institution Fees",
    text:
      "Cover eligible tuition, examination, laboratory, library and other institution-related charges.",
  },
  {
    icon: "bi bi-house",
    title: "Hostel and Living Expenses",
    text:
      "Manage eligible hostel, accommodation, food and day-to-day living expenses during the course.",
  },
  {
    icon: "bi bi-book",
    title: "Books and Equipment",
    text:
      "Finance eligible books, laptops, study materials, equipment and other academic requirements.",
  },
  {
    icon: "bi bi-award",
    title: "Professional Courses",
    text:
      "Explore financing for eligible vocational, technical, certification and career-oriented programmes.",
  },
];

const eligibilityItems = [
  {
    image: "/images/Bank Pages/salaried-image-icon.png",
    alt: "Education-loan student eligibility",
    title: "Student Applicant",
    text:
      "The student should generally be an Indian citizen seeking admission to an eligible course.",
  },
  {
    image: "/images/Bank Pages/age.png",
    alt: "Education-loan student age",
    title: "Student Age",
    text:
      "Age criteria vary according to the lender, course type and selected education-loan product.",
  },
  {
    image: "/images/Bank Pages/3-month-experienced.png",
    alt: "Education admission eligibility",
    title: "Confirmed Admission",
    text:
      "Admission to a recognised institution through merit, entrance examination or approved selection may be required.",
  },
  {
    image: "/images/Bank Pages/minimum-income_20000.png",
    alt: "Education-loan co-applicant",
    title: "Co-Applicant Profile",
    text:
      "A parent, guardian or eligible co-applicant with stable income may be required.",
  },
];

const documentGroups = [
  {
    title: "Student KYC Documents",
    image: "/images/Bank Pages/photo-identy.png",
    items: [
      "PAN Card",
      "Aadhaar Card",
      "Passport",
      "Recent passport-size photographs",
      "Address proof",
      "Passport and visa copy for overseas study, where applicable",
    ],
  },
  {
    title: "Academic Documents",
    image: "/images/Bank Pages/proof-of-residence.png",
    items: [
      "10th and 12th mark sheets",
      "Graduation mark sheets, where applicable",
      "Entrance examination scorecard",
      "Admission letter",
      "Course confirmation letter",
      "Scholarship details, where applicable",
    ],
  },
  {
    title: "Co-Applicant Documents",
    image: "/images/Bank Pages/income-proof.png",
    items: [
      "PAN Card and Aadhaar Card",
      "Recent salary slips",
      "Salary or business bank statements",
      "Form 16",
      "Income tax returns",
      "Business registration and financial statements, where applicable",
    ],
  },
  {
    title: "Course and Supporting Documents",
    image: "/images/Bank Pages/other-document.png",
    items: [
      "Detailed fee structure",
      "Estimated living and travel expenses",
      "Loan application form",
      "Collateral documents, where applicable",
      "Co-applicant relationship proof",
      "Additional lender-specific documents",
    ],
  },
];

const applicationSteps = [
  {
    title: "Share Your Education Plan",
    text:
      "Provide course, institution, country, fee structure and required loan amount.",
  },
  {
    title: "Initial Eligibility Review",
    text:
      "Our team reviews the student profile, admission details, co-applicant income and basic eligibility.",
  },
  {
    title: "Compare Lender Options",
    text:
      "Compare suitable education-loan options based on indicative rates, collateral, tenure and moratorium.",
  },
  {
    title: "Submit Documents",
    text:
      "Submit student, academic, co-applicant, income and course-related documents.",
  },
  {
    title: "Lender Assessment",
    text:
      "The lender verifies admission, course eligibility, co-applicant strength, credit history and security, where applicable.",
  },
  {
    title: "Sanction and Disbursement",
    text:
      "After approval and completion of conditions, funds are disbursed according to the institution and lender schedule.",
  },
];

const educationLoanRates = [
  {
    bank: "Bank of Maharashtra",
    link: "https://bankofmaharashtra.in/educational-loans",
    rate: "7.10%*",
    amount: "As per course and eligibility",
    tenure: "Up to 15 years",
    fee: "As per bank policy",
  },
  {
    bank: "Bank of Baroda",
    link:
      "https://www.bankofbaroda.in/personal-banking/loans/education-loan",
    rate: "7.60%*",
    amount: "Up to ₹80 lakh*",
    tenure: "Up to 15 years",
    fee: "As per bank policy",
  },
  {
    bank: "Central Bank of India",
    link:
      "https://www.centralbankofindia.co.in/en/cent-education-loans",
    rate: "7.80%*",
    amount: "As per course and eligibility",
    tenure: "Up to 15 years",
    fee: "As per bank policy",
  },
  {
    bank: "Indian Overseas Bank",
    link: "https://www.iob.in/Educational-Loan-IOB-Vidya-Shrest",
    rate: "8.25%*",
    amount: "As per product",
    tenure: "As per bank policy",
    fee: "As per bank policy",
  },
  {
    bank: "Bank of India",
    link: "https://bankofindia.co.in/education-loan",
    rate: "8.40%*",
    amount: "As per product",
    tenure: "Up to 15 years",
    fee: "As per bank policy",
  },
  {
    bank: "Credila",
    link: "https://www.credila.com/",
    rate: "As per profile",
    amount: "As per course and eligibility",
    tenure: "As per lender policy",
    fee: "As per lender policy",
  },
];

const coveredExpenses = [
  "Tuition and institutional fees",
  "Hostel or accommodation expenses",
  "Examination, laboratory and library fees",
  "Books, laptop and study equipment",
  "Travel expenses for overseas education",
  "Eligible insurance and other academic costs",
];

const transferBenefits = [
  "Potentially lower interest rate",
  "Possible reduction in repayment burden",
  "More suitable repayment tenure",
  "Top-up facility subject to eligibility",
  "Support with existing-loan documentation",
  "Assistance throughout transfer processing",
];

const faqItems = [
  {
    question: "Who can apply for an education loan?",
    answer:
      "Students admitted to eligible recognised courses in India or abroad may apply, usually with a parent, guardian or other acceptable co-applicant.",
  },
  {
    question: "What expenses can an education loan cover?",
    answer:
      "Eligible expenses may include tuition, hostel, examination, books, equipment, travel and approved living expenses. Coverage depends on lender policy.",
  },
  {
    question: "Is collateral always required?",
    answer:
      "Collateral requirements depend on the loan amount, course, institution, co-applicant profile and lender policy. Some eligible loans may be available without collateral.",
  },
  {
    question: "How much education loan can I receive?",
    answer:
      "The eligible amount depends on course cost, institution, country, co-applicant income, collateral, academic profile and lender assessment.",
  },
  {
    question: "What is a moratorium period?",
    answer:
      "A moratorium is a repayment holiday that may cover the course duration and an additional period after completion. Interest treatment during this period depends on lender terms.",
  },
  {
    question: "When does repayment begin?",
    answer:
      "Repayment usually begins after the applicable moratorium period, according to the lender’s sanction terms.",
  },
  {
    question: "Can I study abroad using an education loan?",
    answer:
      "Yes. Eligible overseas courses and recognised institutions may be financed, subject to admission, visa, course and lender requirements.",
  },
  {
    question: "Can I transfer my existing education loan?",
    answer:
      "Eligible borrowers may transfer an outstanding education loan to another lender for potentially improved terms, subject to assessment and transfer charges.",
  },
  {
    question: "Are processing fees applicable?",
    answer:
      "Processing, documentation, legal, valuation or other charges may apply depending on the lender and loan structure.",
  },
  {
    question: "Can I prepay the loan?",
    answer:
      "Many lenders allow part-prepayment or foreclosure, subject to the terms and charges stated in the sanction letter.",
  },
];

function EducationLoan() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <Seo
        title="Education Loan in Pune | MONAYRA (OPC) PRIVATE LIMITED"
        description="Explore education-loan options with MONAYRA (OPC) PRIVATE LIMITED. Get assistance for studies in India or abroad, eligibility, documents, lender comparison and loan processing."
        keywords="education loan Pune, student loan, study abroad loan, education loan without collateral, higher education finance"
        canonical="https://www.mortbuzz.in/education-loan"
      />

      <main className="monayra-education-loan-page">
        <section className="education-loan-hero">
          <div className="education-loan-hero-overlay"></div>

          <div className="education-loan-hero-container">
            <span className="education-loan-hero-eyebrow animate__animated animate__fadeInDown">
              Education Finance Solutions
            </span>

            <h1 className="animate__animated animate__fadeInUp">
              Invest in your future with the right
              <span> education-loan support</span>
            </h1>

            <p className="animate__animated animate__fadeInUp animate__delay-1s">
              Explore suitable education-loan options for studies in India or
              abroad with assistance for eligibility, documentation and
              lender processing.
            </p>

            <div className="education-loan-hero-actions animate__animated animate__fadeInUp animate__delay-1s">
              <Link
                to="/apply-now"
                className="education-loan-primary-btn"
              >
                Apply for Education Loan
                <i className="bi bi-arrow-right"></i>
              </Link>

              <a
                href="#education-loan-rates"
                className="education-loan-secondary-btn"
              >
                <i className="bi bi-bar-chart"></i>
                View Indicative Rates
              </a>
            </div>

            <div className="education-loan-hero-features">
              <div>
                <i className="bi bi-mortarboard"></i>
                India and overseas studies
              </div>

              <div>
                <i className="bi bi-bank"></i>
                Multiple lender options
              </div>

              <div>
                <i className="bi bi-file-earmark-check"></i>
                Documentation assistance
              </div>
            </div>
          </div>
        </section>

        <section className="education-loan-intro-section">
          <div className="education-loan-page-container education-loan-intro-grid">
            <div className="education-loan-intro-image">
              <img
                src="/images/FrontPage/education-loan-1.png"
                alt="Student receiving education-loan assistance"
              />

              <div className="education-loan-image-overlay"></div>

              <div className="education-loan-rate-card">
                <span>Indicative starting rate</span>
                <strong>7.10%*</strong>
                <small>Subject to lender assessment</small>
              </div>
            </div>

            <div className="education-loan-intro-content">
              <span className="education-loan-section-eyebrow">
                Why Choose MONAYRA?
              </span>

              <h2>
                Financial support for
                <span> ambitious students</span>
              </h2>

              <p>
                MONAYRA (OPC) PRIVATE LIMITED helps students and families
                explore suitable education-loan options from selected banks
                and financial institutions.
              </p>

              <p>
                Our team assists with course and lender comparison,
                documentation, co-applicant requirements, application
                submission and processing updates.
              </p>

              <div className="education-loan-benefits">
                {[
                  "Studies in India and abroad",
                  "Secured and eligible unsecured options",
                  "Course and institution-based assessment",
                  "Moratorium options subject to lender policy",
                  "Co-applicant documentation assistance",
                  "Support until disbursement",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="education-loan-inline-btn"
              >
                Check Your Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="education-loan-uses-section">
          <div className="education-loan-page-container">
            <div className="education-loan-section-header">
              <span className="education-loan-section-eyebrow">
                Eligible Education Expenses
              </span>

              <h2>
                Common uses of an
                <span> education loan</span>
              </h2>

              <p>
                Education finance may cover tuition, accommodation, travel,
                academic equipment and other approved course-related costs.
              </p>
            </div>

            <div className="education-loan-uses-grid">
              {educationLoanUses.map((item) => (
                <article
                  className="education-loan-use-card"
                  key={item.title}
                >
                  <span className="education-loan-use-icon">
                    <i className={item.icon}></i>
                  </span>

                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="education-loan-eligibility-section">
          <div className="education-loan-page-container">
            <div className="education-loan-section-header">
              <span className="education-loan-section-eyebrow">
                Basic Eligibility
              </span>

              <h2>
                Education-loan
                <span> eligibility factors</span>
              </h2>

              <p>
                Lenders evaluate the student, course, institution,
                co-applicant and repayment profile.
              </p>
            </div>

            <div className="education-loan-eligibility-grid">
              {eligibilityItems.map((item) => (
                <article
                  className="education-loan-eligibility-card"
                  key={item.title}
                >
                  <div className="education-loan-eligibility-image">
                    <img
                      src={item.image}
                      alt={item.alt}
                      loading="lazy"
                    />
                  </div>

                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>

            <div className="education-loan-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Final eligibility depends on course recognition, institution,
                co-applicant profile, security and lender policy.
              </span>
            </div>
          </div>
        </section>

        <section className="education-loan-cta-section">
          <div className="education-loan-page-container">
            <div className="education-loan-cta-card">
              <div>
                <span>Start Your Education Journey</span>

                <h2>Ready to finance your higher education?</h2>

                <p>
                  Share your course, institution, country and fee details to
                  explore suitable education-loan options.
                </p>
              </div>

              <Link
                to="/apply-now"
                className="education-loan-primary-btn"
              >
                Apply Now
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="education-loan-documents-section">
          <div className="education-loan-page-container">
            <div className="education-loan-section-header">
              <span className="education-loan-section-eyebrow">
                Document Checklist
              </span>

              <h2>
                Documents generally required for an
                <span> education loan</span>
              </h2>

              <p>
                The final checklist depends on the course, country,
                institution, loan amount and lender requirements.
              </p>
            </div>

            <div className="education-loan-documents-grid">
              {documentGroups.map((group) => (
                <article
                  className="education-loan-document-card"
                  key={group.title}
                >
                  <div className="education-loan-document-heading">
                    <span>
                      <img
                        src={group.image}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                      />
                    </span>

                    <h3>{group.title}</h3>
                  </div>

                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>
                        <i className="bi bi-check-circle"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="education-loan-process-section">
          <div className="education-loan-page-container">
            <div className="education-loan-section-header">
              <span className="education-loan-section-eyebrow">
                Application Journey
              </span>

              <h2>
                A simple education-loan
                <span> application process</span>
              </h2>
            </div>

            <div className="education-loan-process-grid">
              {applicationSteps.map((step, index) => (
                <article
                  className="education-loan-process-card"
                  key={step.title}
                >
                  <span className="education-loan-process-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="education-loan-rates"
          className="education-loan-rates-section"
        >
          <div className="education-loan-page-container">
            <div className="education-loan-section-header">
              <span className="education-loan-section-eyebrow">
                Indicative Bank Comparison
              </span>

              <h2>
                Education-loan
                <span> interest-rate overview</span>
              </h2>

              <p>
                Rates, amounts, tenure and charges are indicative and must be
                confirmed with the selected lender.
              </p>
            </div>

            <div className="education-loan-table-wrapper">
              <table className="education-loan-rate-table">
                <thead>
                  <tr>
                    <th>Lender</th>
                    <th>Starting Rate</th>
                    <th>Indicative Amount</th>
                    <th>Maximum Tenure</th>
                    <th>Processing Fee</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {educationLoanRates.map((bank) => (
                    <tr key={bank.bank}>
                      <td>
                        <a
                          href={bank.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {bank.bank}
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      </td>

                      <td>
                        <strong>{bank.rate}</strong>
                      </td>

                      <td>{bank.amount}</td>
                      <td>{bank.tenure}</td>
                      <td>{bank.fee}</td>

                      <td>
                        <Link
                          to="/apply-now"
                          className="education-loan-table-btn"
                        >
                          Apply
                          <i className="bi bi-arrow-right"></i>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="education-loan-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Final terms depend on the course, institution, applicant,
                co-applicant and lender assessment.
              </span>
            </div>
          </div>
        </section>

        <section className="education-loan-expenses-section">
          <div className="education-loan-page-container education-loan-expenses-grid">
            <div className="education-loan-expenses-content">
              <span className="education-loan-section-eyebrow">
                What May Be Covered?
              </span>

              <h2>
                Plan for the complete
                <span> cost of education</span>
              </h2>

              <p>
                Education-loan coverage may extend beyond tuition fees,
                depending on the course and lender policy.
              </p>

              <div className="education-loan-expenses-list">
                {coveredExpenses.map((item) => (
                  <div key={item}>
                    <i className="bi bi-check-circle"></i>
                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="education-loan-inline-btn"
              >
                Discuss Your Education Cost
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>

            <div className="education-loan-expenses-image">
              <img
                src="/images/Bank Pages/education-loan.png"
                alt="Student education expenses"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="education-loan-fee-section">
          <div className="education-loan-page-container education-loan-fee-grid">
            <div className="education-loan-fee-image">
              <img
                src="/images/FrontPage/loan-dealing.jpg"
                alt="Education-loan processing"
                loading="lazy"
              />
            </div>

            <div className="education-loan-fee-content">
              <span className="education-loan-section-eyebrow">
                Loan Charges
              </span>

              <h2>
                Understand the
                <span> processing fee</span>
              </h2>

              <p>
                Lenders may charge processing fees to verify admission,
                academic, co-applicant, income and security documents.
              </p>

              <p>
                The exact amount depends on the lender, loan amount, course,
                institution, country and applicable offer.
              </p>

              <div className="education-loan-fee-highlight">
                <i className="bi bi-receipt"></i>

                <div>
                  <strong>Review all applicable charges</strong>

                  <span>
                    Confirm processing, legal, valuation, documentation and
                    other lender charges before applying.
                  </span>
                </div>
              </div>

              <Link
                to="/apply-now"
                className="education-loan-inline-btn"
              >
                Get Suitable Options
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="education-loan-transfer-section">
          <div className="education-loan-page-container education-loan-transfer-grid">
            <div className="education-loan-transfer-content">
              <span className="education-loan-section-eyebrow">
                Existing Loan Transfer
              </span>

              <h2>
                Explore an education-loan
                <span> balance transfer</span>
              </h2>

              <p>
                An eligible borrower may transfer an outstanding education
                loan to another lender for potentially improved repayment
                terms.
              </p>

              <div className="education-loan-benefits">
                {transferBenefits.map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="education-loan-inline-btn"
              >
                Check Transfer Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>

            <div className="education-loan-transfer-image">
              <img
                src="/images/Bank Pages/education-loan.png"
                alt="Education-loan balance transfer"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="education-loan-faq-section">
          <div className="education-loan-page-container">
            <div className="education-loan-section-header">
              <span className="education-loan-section-eyebrow">
                Common Questions
              </span>

              <h2>
                Education-loan
                <span> frequently asked questions</span>
              </h2>
            </div>

            <div className="education-loan-faq-layout">
              <div className="education-loan-faq-list">
                {faqItems.map((faq, index) => {
                  const isOpen = openFaq === index;

                  return (
                    <article
                      className={`education-loan-faq-item ${
                        isOpen ? "open" : ""
                      }`}
                      key={faq.question}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenFaq(isOpen ? null : index)
                        }
                        aria-expanded={isOpen}
                      >
                        <span className="education-loan-faq-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span>{faq.question}</span>

                        <i
                          className={
                            isOpen
                              ? "bi bi-dash-lg"
                              : "bi bi-plus-lg"
                          }
                        ></i>
                      </button>

                      {isOpen && (
                        <div className="education-loan-faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <aside className="education-loan-help-card">
                <span className="education-loan-help-icon">
                  <i className="bi bi-headset"></i>
                </span>

                <span className="education-loan-help-eyebrow">
                  Need Assistance?
                </span>

                <h3>Speak with our education-loan team</h3>

                <p>
                  Share your course, institution, country, fee structure and
                  co-applicant details to understand suitable next steps.
                </p>

                <Link
                  to="/contact"
                  className="education-loan-primary-btn"
                >
                  Contact Our Team
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/apply-now"
                  className="education-loan-help-secondary"
                >
                  <i className="bi bi-file-earmark-text"></i>
                  Apply Online
                </Link>
              </aside>
            </div>
          </div>
        </section>

        <section className="education-loan-disclaimer">
          <div className="education-loan-page-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              *Displayed rates, amounts and product details are indicative.
              Final approval, interest rate, tenure, loan amount, moratorium,
              collateral, fees and other terms are determined by the selected
              lender after complete assessment. MONAYRA (OPC) PRIVATE LIMITED
              provides loan assistance and does not guarantee approval.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

export default EducationLoan;